import boardTrustedNumbersMd from "./what-would-be-possible-if-your-board-actually-trusted-the-numbers.md?raw";
import threeThingsCfosMd from "./three-things-cfos-cant-stop-talking-about.md?raw";
import taiSoftwareCommissionsMd from "./tai-software-governed-commission-system.md?raw";
import mostBiToolsMd from "./most-bi-tools-ask-you-to-trust-the-data.md?raw";
import nobodyOwnsYourDataProblemMd from "./nobody-owns-your-data-problem.md?raw";
import nobodyCaresAboutYourSlidesMd from "./nobody-cares-about-your-slides.md?raw";
import needACoWorkerMd from "./your-customer-doesnt-need-an-expert-they-need-a-co-worker.md?raw";
import tailoredNotCustomizableMd from "./tailored-not-customizable.md?raw";
import useAiToAutomateMd from "./use-ai-to-automate-the-tasks-that-suck.md?raw";
import buildItYourselfMd from "./you-could-build-your-next-ai-workflow-yourself.md?raw";

export interface BlogPostShareImage {
  path: string;
  width: number;
  height: number;
  alt: string;
}

export interface BlogPost {
  slug: string;
  title: string;
  description: string;
  date: string;
  displayDate: string;
  excerpt: string;
  shareImage: BlogPostShareImage | null;
  body: string;
}

// Posts render in array order on /blog (keep newest / lead post first). To add a post: drop a
// markdown file in this folder (headings start at ##; the page supplies the H1), import it with
// ?raw, add an entry here, and add the URL to public/sitemap.xml.
export const blogPosts: BlogPost[] = [
  {
    slug: "what-would-be-possible-if-your-board-actually-trusted-the-numbers",
    title: "What Would Be Possible If Your Board Actually Trusted the Numbers?",
    description:
      "Most board meetings lose their first 45 minutes to arguing about whose ARR is right. When every number traces to its source, that time goes to faster decisions, bolder bets, and strategy instead of spreadsheets.",
    date: "2026-10-19",
    displayDate: "Oct 19, 2026",
    excerpt:
      "Slide four shows ARR. Someone asks why it doesn't match last quarter's deck. Forty-five minutes later the team has a reconciliation task, and the decision on slide twelve gets pushed to next quarter. If the arguing about the numbers stopped, what would that time go to instead?",
    shareImage: null,
    body: boardTrustedNumbersMd,
  },
  {
    slug: "three-things-cfos-cant-stop-talking-about",
    title: "Three Things CFOs Can't Stop Talking About (And What to Actually Do About Them)",
    description:
      "Across a dozen conversations at a CFO conference, the same three frustrations kept surfacing: systems that don't agree, reports consolidated by hand, and no budget or time to figure out AI. Here's what DataFabrIQ does about each one.",
    date: "2026-10-14",
    displayDate: "Oct 14, 2026",
    excerpt:
      "Last week I was at a CFO conference, and across a dozen conversations the same three frustrations kept surfacing — not the strategic, big-picture stuff from the keynotes, but the grind that eats a finance leader's actual week.",
    shareImage: {
      path: "/images/blog/three-things-cfos-cant-stop-talking-about.jpg",
      width: 2000,
      height: 1316,
      alt: "What CFOs told me versus what DataFabrIQ does: one governed model, one automated process at a time, and an FDE who fixes the root so your team runs it.",
    },
    body: threeThingsCfosMd,
  },
  {
    slug: "tai-software-governed-commission-system",
    title:
      "Tai Software: From a Fragile Spreadsheet to a Governed Commission System — Live in Six Weeks",
    description:
      "How Tai Software replaced a hand-maintained 19-tab commission workbook with a governed engine that ties to Finance’s signed statements to the dollar — and got pipeline review, sales team reporting, and an ARR rollforward out of the same six-week project.",
    date: "2026-10-01",
    displayDate: "Oct 1, 2026",
    excerpt:
      "Tai’s comp plan has tiered rates, a waivable bonus gate, 12-month cohort windows, rate preservation, and retro-tiering on churn — and all of it lived in formulas in one workbook maintained by hand. Six weeks after kickoff, the May commission run went out of DataFabrIQ instead.",
    shareImage: null,
    body: taiSoftwareCommissionsMd,
  },
  {
    slug: "nobody-owns-your-data-problem",
    title: "Nobody Owns Your Data Problem. That's the Problem.",
    description:
      "HubSpot says one number, finance says another, and nobody's whole job is to fix it. Meet the Forward Deployed Expert: someone embedded in your business who eats the pain, fixes the root definitions, and hands back working systems.",
    date: "2026-09-22",
    displayDate: "Sep 22, 2026",
    excerpt:
      "Most companies do not really have a data problem. They have a \"nobody owns this\" problem. Everyone can see the mess and nobody's whole job is to clean it up, so it stays. Then you bolt on another AI tool and it just automates the confusion faster.",
    shareImage: {
      path: "/images/blog/nobody-owns-your-data-problem.jpg",
      width: 1280,
      height: 720,
      alt: "Eat the pain. Output value. A Forward Deployed Expert funnels tangled spreadsheets and CRM errors into clean, reconciled data.",
    },
    body: nobodyOwnsYourDataProblemMd,
  },
  {
    slug: "your-customer-doesnt-need-an-expert-they-need-a-co-worker",
    title: "Your Customer Doesn't Need an Expert, They Need a Co-Worker.",
    description:
      "Customer success used to run on storytelling, because storytelling was the only lever I had. AI gave me real leverage — and traded the expert act for something better: building alongside customers instead of presenting to them.",
    date: "2026-08-31",
    displayDate: "Aug 31, 2026",
    excerpt:
      "A controller Slacked me because his Claude connection had dropped and he needed his numbers checked before a big meeting. So I checked them, right there in the channel we already shared. It was shocking how normal it felt — not like helping a client, but like helping a co-worker.",
    shareImage: null,
    body: needACoWorkerMd,
  },
  {
    slug: "nobody-cares-about-your-slides",
    title: "Nobody Cares About Your Slides",
    description:
      "Why we swapped customer slide decks for a plain weekly doc — what shipped, what slipped, what's blocked — built with Claude in an afternoon and shared live over Slack.",
    date: "2026-08-17",
    displayDate: "Aug 17, 2026",
    excerpt:
      "I showed up to a roadmap meeting with a little homemade HTML doc and, instead of dressing anything up, just said plainly: here's what's done, here's what isn't. By the end of the call she asked for a copy — not of a fancy deck, but of the brutally honest, kinda ugly one.",
    shareImage: null,
    body: nobodyCaresAboutYourSlidesMd,
  },
  {
    slug: "tailored-not-customizable",
    title: "Tailored, Not Customizable",
    description:
      "Customizable means you bend your business to fit the tool. Tailored means the tool learns your business — your MRR definition, your commission rules, your variance lines — encoded, not configured.",
    date: "2026-08-12",
    displayDate: "Aug 12, 2026",
    excerpt:
      "\"Customizable\" means you bend your business to fit the tool. \"Tailored\" means the tool learns your business. That's not a word game. It's the whole difference.",
    shareImage: {
      path: "/images/blog/tailored-not-customizable.png",
      width: 2000,
      height: 1250,
      alt: "Customizable versus tailored — the same business shape: one hangs off a one-size template, gaps and all; the other is cut to your exact measurements",
    },
    body: tailoredNotCustomizableMd,
  },
  {
    slug: "you-could-build-your-next-ai-workflow-yourself",
    title: "You Could Probably Build Your Next AI Workflow Yourself. Here's Why You Shouldn't.",
    description:
      "The build is the easy part — owning the fragile machine forever is the part nobody quotes you on. Why AI workflows need a governed, auditable data foundation, not another weekend prototype.",
    date: "2026-07-10",
    displayDate: "Jul 10, 2026",
    excerpt:
      "Every leader has had the thought: I could build this report myself. And you're not wrong — you probably could. The problem isn't whether you can build it. It's what you own the day after you do.",
    shareImage: {
      path: "/images/blog/build-it-yourself-vs-datafabriq.png",
      width: 2760,
      height: 1800,
      alt: "Build it yourself — taped-together, breaks silently — versus DataFabrIQ: unified, auditable, one source of truth",
    },
    body: buildItYourselfMd,
  },
  {
    slug: "most-bi-tools-ask-you-to-trust-the-data",
    title: "Most BI tools ask you to trust the data. We think you shouldn't have to.",
    description:
      "Introducing DataFabrIQ: one auditable data model across CRM, billing, accounting, and payments — and purpose-built workflows that defend every number underneath your dashboards.",
    date: "2026-07-06",
    displayDate: "Jul 6, 2026",
    excerpt:
      "The most important workflows — MRR close, pipeline review, commission runs — are run in a tangle of spreadsheets, scripts, and Slack threads. The answer isn't another dashboard. So we built the underneath.",
    shareImage: {
      path: "/images/blog/the-dashboard-is-the-last-mile.jpg",
      width: 1600,
      height: 900,
      alt: "The dashboard is the last mile. We defend the first: the data underneath it.",
    },
    body: mostBiToolsMd,
  },
  {
    slug: "use-ai-to-automate-the-tasks-that-suck",
    title: "Use AI to Automate the Tasks That Suck (And Empower the Humans Who Aren't Going Anywhere)",
    description:
      "AI won't replace your finance team. It should take the reconciliations, commission runs, and rebuilt spreadsheets off their plate — and here's why most AI never makes it past the demo.",
    date: "2026-07-06",
    displayDate: "Jul 6, 2026",
    excerpt:
      "The zeitgeist argues \"AI will replace your whole team\" versus \"it's all hype\". Both takes are loud, both are wrong. The real conversation: handing AI the tasks you don't want your team spinning cycles on in the first place.",
    shareImage: null,
    body: useAiToAutomateMd,
  },
];
